const HTTP_STATUS_MESSAGE = /^response not successful: received status code \d+$/i;

type GraphError = { message?: string | null };

export function extractGraphqlMessages(error: unknown): string[] {
  const messages: string[] = [];
  const seen = new Set<string>();

  function add(value?: string | null) {
    const cleaned = cleanErrorMessage(value);
    if (!cleaned || HTTP_STATUS_MESSAGE.test(cleaned) || seen.has(cleaned)) {
      return;
    }
    seen.add(cleaned);
    messages.push(cleaned);
  }

  if (!error) {
    return messages;
  }
  if (typeof error === 'string') {
    add(error);
    return messages;
  }
  if (typeof error !== 'object') {
    return messages;
  }

  const record = error as Record<string, unknown>;
  addGraphErrors(record.graphQLErrors, add);
  addGraphErrors(record.clientErrors, add);
  addGraphErrors(record.protocolErrors, add);
  addFromNetwork(record.networkError, add);

  if (error instanceof Error) {
    add(error.message);
  }

  return messages;
}

export function cleanErrorMessage(value?: string | null): string {
  if (!value) {
    return '';
  }
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+\n/g, '\n')
    .replace(/\n\s+/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
}

function addFromNetwork(network: unknown, add: (value?: string | null) => void) {
  if (!network) {
    return;
  }
  if (typeof network === 'string') {
    add(network);
    return;
  }
  if (typeof network !== 'object') {
    return;
  }
  const record = network as Record<string, unknown>;
  addFromResult(record.result, add);
  addFromResult(record.body, add);
  if (network instanceof Error) {
    add(network.message);
  }
}

function addFromResult(result: unknown, add: (value?: string | null) => void) {
  if (!result) {
    return;
  }
  if (typeof result === 'string') {
    add(result);
    return;
  }
  if (Array.isArray(result)) {
    addGraphErrors(result, add);
    return;
  }
  if (typeof result !== 'object') {
    return;
  }
  const record = result as Record<string, unknown>;
  addGraphErrors(record.errors, add);
  if (typeof record.message === 'string') {
    add(record.message);
  }
  if (typeof record.error === 'string') {
    add(record.error);
  }
}

function addGraphErrors(value: unknown, add: (message?: string | null) => void) {
  if (!Array.isArray(value)) {
    return;
  }
  for (const item of value) {
    if (typeof item === 'string') {
      add(item);
      continue;
    }
    if (item && typeof item === 'object' && 'message' in item) {
      add((item as GraphError).message);
    }
  }
}
